import {dashboard} from "../global";
import axios from "axios";
import {getStoredSession} from "../LocalStorageService";
import {_buildQueryString, CACHE_BYPASS_PARAMS, cacheGet, cacheInvalidatePrefix, cacheSet} from "./cacheUtils";

// ── Internal helpers ─────────────────────────────────────────────────────────

/** Normalises jobIds to SaaS format. Returns {job_id: int|int[]} or {} for spreading into request. */
function _jobIdParam(jobIds) {
    if (!jobIds || (typeof jobIds === 'object' && !Array.isArray(jobIds))) return {};
    const arr = Array.isArray(jobIds) ? jobIds : [jobIds];
    const ints = arr.map(id => parseInt(id, 10)).filter(n => !isNaN(n));
    if (!ints.length) return {};
    return {job_id: ints.length === 1 ? ints[0] : ints};
}

/**
 * Normalises an explicit customer id (the SaaS `cid` = target_company_id, "scope to company Y").
 * Returns {cid: int} when valid, or {} otherwise. MUST return {} (not {cid: undefined}) so that an
 * absent value falls through to _deriveCID() via the `{cid, ...request}` spread in postDashboard.
 */
function _cidParam(cid) {
    const n = parseInt(cid, 10);
    return isNaN(n) ? {} : {cid: n};
}

/** Derives CID from localStorage. Admin → 0, Customer → arkbridge_customer_id. */
function _deriveCID() {
    const user = getStoredSession()?.user;
    return user?.features?.includes('admin_dashboard') ? 0 : user?.arkbridge_customer_id;
}

/**
 * Internal post to the dashboard proxy endpoint.
 * All dashboard-type SaaS API calls go through this single function.
 * @param {string} type - SaaS API request type
 * @param {object} request - Request payload. If cid is present in request it takes priority, otherwise derived from session.
 * @param {object} [options]
 * @param {boolean} [options.cache=true] - Whether the answer may be served from, and written to, the cache.
 * @param {AbortSignal} [options.signal] - Aborts the request, and opts it out of in-flight deduplication.
 * @param {boolean} [options.background=false] - Marks a request nobody is waiting for, so that it does not
 *   raise the global loading wash. Read off the axios config by the interceptor in index.js; passed
 *   through as config rather than as a payload field, since the SaaS API has no use for it.
 * @param {boolean} [options.quiet=false] - Marks a request whose failure is not news, so that a 400 or a
 *   404 does not raise the global error banner. For optional data the caller displays the absence of; see
 *   requestNotices.js. Travels as config for the same reason `background` does.
 * @returns {Promise<object>} - Resolved response.data
 */
const _inflight = new Map(); // cacheKey → in-flight Promise (deduplicates simultaneous identical calls)

function postDashboard(type, request = {}, { cache = true, signal, background = false, quiet = false } = {}) {
    const cid = request.cid ?? _deriveCID();
    const fullRequest = {cid, ...request};

    // strip empty-like values before bypass check — same rule as _buildQueryString
    const effectiveRequest = Object.fromEntries(
        Object.entries(fullRequest).filter(([, v]) =>
            v !== null && v !== undefined && v !== '' &&
            !(Array.isArray(v) && v.length === 0)
        )
    );

    const requestKey = `dashboard:${type}${_buildQueryString(effectiveRequest)}`;
    const isCacheable = cache &&
        !Object.keys(effectiveRequest).some(k => CACHE_BYPASS_PARAMS.has(k));
    const cacheKey = isCacheable ? requestKey : null;

    if (cacheKey) {
        const cached = cacheGet(cacheKey);
        if (cached !== null) return Promise.resolve(cached);
    }

    const shouldDeduplicate = !signal;
    if (shouldDeduplicate && _inflight.has(requestKey)) return _inflight.get(requestKey);

    const promise = axios.post(`${dashboard}?action=${type}`, {type, request: fullRequest}, {signal, background, quiet})
        .then(response => {
            if (![200].includes(Number(response?.status))) throw new Error(`Unexpected status code: ${response?.status}`);
            const data = response?.data;
            if (cacheKey && data !== undefined) cacheSet(cacheKey, data);
            return data;
        })
        .finally(() => { if (shouldDeduplicate) _inflight.delete(requestKey); });

    if (shouldDeduplicate) _inflight.set(requestKey, promise);
    return promise;
}

/** Flattens nested result structure: Object.values(data.result) → concat all v.data arrays */
function _extractRows(data) {
    let rows = [];
    Object.values(data?.result || {}).forEach(v => { if (v?.data) rows = rows.concat(v.data); });
    return rows;
}

/** Extracts count from result.data.count */
function _extractCount(data) {
    return data?.result?.data?.count ? Number(data.result.data.count) : 0;
}

/** Finds first entry in result that has data */
function _extractEntry(data) {
    const entry = Object.values(data?.result || {}).find(v => v?.data);
    return entry?.data || null;
}

// ── File Data ────────────────────────────────────────────────────────────────

export function fetchFileCountData(search = "", filterData = {}, jobIds, cid, options = {}) {
    return postDashboard('man_search_file_count', {search: search || undefined, ...filterData, ..._jobIdParam(jobIds), ..._cidParam(cid)}, options)
        .then(_extractCount)
        .catch(error => { console.error(error); throw error; });
}

export function fetchFileTypeData(jobIds, cid, options = {}) {
    return postDashboard('rep_filetype', {..._jobIdParam(jobIds), ..._cidParam(cid)}, options)
        .then(_extractRows)
        .catch(error => { console.error(error); throw error; });
}

export function fetchFileData(pageNumber, search = "", filterData = {}, jobIds, cid, options = {}) {
    return postDashboard('man_search_file', {page_number: pageNumber - 1, page_size: 10, search: search || undefined, file_properties_as_attributes: true, ...filterData, ..._jobIdParam(jobIds), ..._cidParam(cid)}, options)
        .then(_extractRows)
        .catch(error => { console.error(error); throw error; });
}

export function fetchFileMetadata() {
    return postDashboard('file_metadata')
        .then(data => {
            let resultsObj = {};
            Object.values(data?.result || {}).forEach((value) => {
                Object.entries(value || {}).forEach(
                    ([metadataKey, metadataListValue]) =>
                        resultsObj[metadataKey] = [...(resultsObj[metadataKey] || []), ...(metadataListValue ? (Array.isArray(metadataListValue) ? metadataListValue : Object.entries(metadataListValue).map(([key, value]) => ({key, value}))) : [])]
                );
            });
            return resultsObj;
        })
        .catch(error => { console.error("Error fetching file metadata:", error); throw error; });
}

// ── Backup Source ────────────────────────────────────────────────────────────

export function fetchBackupSourceData(backupSourceId) {
    return postDashboard('backup_source', {backup_source_id: backupSourceId})
        .then(data => {
            const entry = Object.values(data?.result ?? {}).map(x => x?.data).filter(Boolean);
            return entry[0] ?? {};
        })
        .catch(error => { console.error(error); throw error; });
}

// ── Tape Count & Data ────────────────────────────────────────────────────────

/**
 * Separates a media filter from the options meant for the request itself.
 *
 * The three calls below take the same filter fields the Media Metrics descriptors carry — `statusIds`,
 * `metadataIncludes`, `metadataExcludes` — so a clicked tile's criteria reach the Data Management tape
 * list as the SaaS parameters they mean, and not as stray keys in an axios config.
 *
 * One helper for all three because they have to agree: the list and the count are drawn side by side as
 * rows and a total, and a filter applied to one of them but not the other reads as a page of ten under a
 * total of six thousand.
 *
 * @param {object} [options] - Filter fields mixed in with postDashboard options.
 * @returns {{filterParams: object, requestOptions: object}} SaaS parameters, and what is left over.
 */
const _splitMediaFilter = (options = {}) => {
    const {statusIds, metadataIncludes, metadataExcludes, ...requestOptions} = options;
    return {
        filterParams: {..._statusIdParam(statusIds), ..._metadataFilterParams({metadataIncludes, metadataExcludes})},
        requestOptions,
    };
};

export function fetchTapeCountData(search = "", jobIds, cid, options = {}) {
    const {filterParams, requestOptions} = _splitMediaFilter(options);
    return postDashboard('man_item_count', {search: search || undefined, ...filterParams, ..._jobIdParam(jobIds), ..._cidParam(cid)}, requestOptions)
        .then(_extractCount)
        .catch(error => { console.error(error); throw error; });
}

export function fetchTapeData(pageNumber, search = "", jobIds, cid, options = {}) {
    const {filterParams, requestOptions} = _splitMediaFilter(options);
    return postDashboard('man_item_list', {page_number: pageNumber - 1, page_size: 10, search: search || undefined, ...filterParams, ..._jobIdParam(jobIds), ..._cidParam(cid)}, requestOptions)
        .then(_extractRows)
        .catch(error => { console.error(error); throw error; });
}

export function fetchTapeDataWithCount(pageNumber, search = "", jobIds, cid, options = {}) {
    const {filterParams, requestOptions} = _splitMediaFilter(options);
    return postDashboard('man_item_list', {page_number: pageNumber - 1, page_size: 10, search: search || undefined, ...filterParams, ..._jobIdParam(jobIds), ..._cidParam(cid)}, requestOptions)
        .then(data => {
            const rows = _extractRows(data);
            const rawCount = data?.result?.data?.count;
            const count = rawCount !== undefined && rawCount !== null ? Number(rawCount) : null;
            return {
                rows,
                count: Number.isFinite(count) ? count : null,
            };
        })
        .catch(error => { console.error(error); throw error; });
}

/**
 * Rows per request when sweeping the whole media list for a report.
 *
 * Not one enormous request: the SaaS puts no ceiling on `page_size` (see QueryFulfiller), but the
 * response comes back through API Gateway, which caps a proxy response at 6 MB — a single request for a
 * hundred thousand rows does not fail cleanly, it fails as a truncated body. Five hundred rows keeps
 * each response small while asking ArkBridge for two orders of magnitude fewer round trips than the
 * table's page size would.
 */
export const REPORT_EXPORT_BATCH_SIZE = 500;

/**
 * Ceiling on rows in one exported report.
 *
 * A customer with nothing selected in the header is asking for every tape they own, which for a large
 * account is hundreds of thousands of rows — minutes of querying, and a workbook the browser builds
 * entirely in memory before it can be saved. Stopping at fifty thousand keeps a mis-click from becoming
 * a tab that hangs; the caller is told when it happened rather than being handed a silently short file.
 */
export const REPORT_EXPORT_ROW_LIMIT = 50000;

/**
 * Reads the whole media list for a report, a batch at a time.
 *
 * Sequential rather than parallel on purpose. The batches are large and each is a paged query over
 * vwItemsAPI; firing twenty at once to save wall-clock would land the whole cost of one operator's
 * export on ArkBridge simultaneously, which is exactly what the periodic refresh on the processing
 * tab was careful not to do. It also means `signal` actually stops the work, rather than aborting
 * twenty in-flight requests that ArkBridge is already executing.
 *
 * Uncached, because a report is a statement about now — and because the batches would otherwise sit in
 * the dashboard cache for ten minutes, evicting the pages the rest of the portal is using.
 *
 * @param {object} scope - {jobIds, cid, search} — the same scoping the counters and the table use.
 * @param {object} [options] - {signal, onProgress({fetched, total}), batchSize, rowLimit}
 * @returns {Promise<{rows: object[], count: number, truncated: boolean}>} `truncated` is true when the
 *          row limit stopped the sweep before the end of the list.
 */
export async function fetchTapeDataForReport({jobIds, cid, search = ''} = {}, {signal, onProgress, batchSize = REPORT_EXPORT_BATCH_SIZE, rowLimit = REPORT_EXPORT_ROW_LIMIT} = {}) {
    const scope = {search: search || undefined, ..._jobIdParam(jobIds), ..._cidParam(cid)};
    const rows = [];
    let total = null;
    let pageNumber = 0;
    let exhausted = false;

    while (!exhausted && rows.length < rowLimit) {
        const data = await postDashboard('man_item_list', {...scope, page_number: pageNumber, page_size: batchSize}, {cache: false, signal});
        const batch = _extractRows(data);

        if (total === null) {
            const rawCount = Number(data?.result?.data?.count);
            total = Number.isFinite(rawCount) ? rawCount : null;
        }
        rows.push(...batch);
        if (onProgress) onProgress({fetched: rows.length, total});

        /*
         * A short batch is the end of the list — the count is not what ends the loop. The two are
         * separate queries against a table being written to, so they can disagree; trusting the count
         * would mean requesting empty page after empty page whenever it read high, and stopping early
         * whenever it read low.
         */
        exhausted = batch.length < batchSize;
        pageNumber += 1;
    }

    return {
        rows: rows.slice(0, rowLimit),
        count: Number.isFinite(total) ? total : rows.length,
        // Only truncated if there is known to be more. A list whose last batch happens to fill the
        // limit exactly is complete, and the count is what distinguishes that from a real cut-off.
        truncated: !exhausted && (total === null || rows.length < total),
    };
}

/**
 * Rows per page in the job tape table, and the divisor the view needs to turn a total into a page
 * count. One constant so the two can never disagree — they used to be two literal 10s.
 */
export const JOB_TAPE_PAGE_SIZE = 10;

/**
 * Ordering for the job tape table: most recently modified media first, newest created breaking ties.
 *
 * So that page one is the page worth polling. The SaaS default is `CompanyID, JobID, ItemID`, which
 * puts the oldest tape of the job first and buries anything that just changed on the last page — an
 * operator watching a read move through cannot see it happen. Reversed, the tapes that changed in the
 * time since the last tick are the ones on screen.
 *
 * `updated` maps to `try_cast(vi.updatedate as datetime)` in ItemListFulfiller, not to a plain column:
 * ArkBridge stores that stamp as a varchar ('Aug 20 2024  6:32AM'), which sorts by month name if
 * ordered as text. `item_id desc` is the tie-break, because updatedate has minute resolution and a
 * whole audit's worth of tapes can share one value.
 */
const JOB_TAPE_ORDER_BY = 'updated desc,item_id desc';

/*
 * Cleared the first time the SaaS rejects the ordering, and not retried for the rest of the session.
 *
 * `order_by` is validated against a whitelist in ItemListFulfiller and an unrecognised field is a 400,
 * so a UI newer than the SaaS it is talking to would blank the tape table outright. Falling back to the
 * default ordering costs the operator the useful sort; failing costs them the table. The flag is what
 * keeps that from being two requests every time rather than two requests once.
 */
let _jobTapeOrderingSupported = true;

function _fetchJobTapeList(cid, jobId, pageNumber, options = {}) {
    const {statusIds, metadataIncludes, metadataExcludes, ...listOptions} = options;
    const request = {
        cid,
        job_id: jobId,
        page_number: pageNumber - 1,
        page_size: JOB_TAPE_PAGE_SIZE,
        ..._statusIdParam(statusIds),
        ..._metadataFilterParams({metadataIncludes, metadataExcludes}),
    };
    if (!_jobTapeOrderingSupported) return postDashboard('man_item_list', request, listOptions);
    return postDashboard('man_item_list', {...request, order_by: JOB_TAPE_ORDER_BY}, listOptions)
        .catch(error => {
            // Only a 400 is read as "this SaaS does not know that ordering field". An aborted request
            // carries no status at all, and a 5xx is a real failure the caller should still see.
            if (Number(error?.response?.status) !== 400) throw error;
            _jobTapeOrderingSupported = false;
            console.warn("SaaS rejected the tape ordering; falling back to its default order.", error);
            return postDashboard('man_item_list', request, listOptions);
        });
}

/**
 * Drops every cached page of one job's tape list.
 *
 * For the caller that has just noticed the list changed. Ordering by last-modified means rows migrate
 * between pages — a tape read on page two moves to page one and pushes everything below it down a
 * place — so a page cached before that happened can repeat a row that has moved up or skip one that
 * moved down. Page one is read live and stays right; the pages behind it are what go stale.
 *
 * Prefix invalidation rather than a key per page, because the number of cached pages is not known
 * here: the cache key sorts its params alphabetically, which puts `cid` and `job_id` ahead of
 * `order_by`, `page_number` and `page_size`, so one prefix covers every page of this job and touches
 * no other job. The trailing '&' is what keeps job 51 from also clearing job 512.
 *
 * @param {number} cid - Company scope, as sent on the list requests.
 * @param {number|string} jobId - The job whose pages should be dropped.
 */
export function invalidateJobTapeListCache(cid, jobId) {
    cacheInvalidatePrefix(`dashboard:man_item_list${_buildQueryString({cid, job_id: jobId})}&`);
}

/**
 * One page of a job's tape list, together with the length of the whole list.
 *
 * The count comes from `man_item_count` rather than from the list response, which is the only way to get
 * it: `ItemListFulfiller` sets its `count` to the number of rows it is returning — `process_results` takes
 * it from the cursor's row count — so a `page_size: 10` request answers `count: 10` whether the job holds
 * eleven tapes or ninety thousand. Read from the list, the paginator concluded there was one page for
 * every job, and the pages past the first were unreachable.
 *
 * Two requests per page load, then, and the second is the same `man_item_count` the tiles above the table
 * are already built from — same filters, same scope — so a filtered table and the tile that opened it
 * cannot report different totals.
 *
 * A failed count does not fail the page: the rows are what the reader came for, and `null` tells the view
 * to fall back to what it can see rather than to show an error over a table that loaded.
 *
 * @param {number} cid - Company scope.
 * @param {number|string} jobId - The job whose media is listed.
 * @param {number} pageNumber - 1-based page.
 * @param {object} [options] - Passed to postDashboard, plus the filter fields `statusIds`,
 *        `metadataIncludes` and `metadataExcludes`, which are applied to both requests.
 * @returns {Promise<{rows: object[], count: number|null}>}
 */
export function fetchJobTapeDataWithCount(cid, jobId, pageNumber, options = {}) {
    const {statusIds, ...countOptions} = options;
    return Promise.all([
        _fetchJobTapeList(cid, jobId, pageNumber, options),
        fetchJobTapeStatusCount(cid, jobId, statusIds, countOptions)
            .catch(error => {
                console.warn("Could not read the tape total; falling back to the page length.", error);
                return null;
            }),
    ])
        .then(([data, total]) => {
            const rows = _extractRows(data).slice(0, JOB_TAPE_PAGE_SIZE);
            const count = total === null || total === undefined ? null : Number(total);
            return {
                rows,
                count: Number.isFinite(count) ? count : null,
            };
        })
        .catch(error => { console.error("fetchJobTapeDataWithCount error:", error); throw error; });
}

// ── Job Processing (SaaS tape data by job) ───────────────────────────────────

export function fetchJobTapeCount(cid, jobId, options = {}) {
    return postDashboard('man_item_count', {cid, job_id: jobId}, options)
        .then(_extractCount)
        .catch(error => { console.error("fetchJobTapeCount error:", error); throw error; });
}

export function fetchJobTapeData(cid, jobId, pageNumber, options = {}) {
    return _fetchJobTapeList(cid, jobId, pageNumber, options)
        .then(data => _extractRows(data).slice(0, JOB_TAPE_PAGE_SIZE))
        .catch(error => { console.error("fetchJobTapeData error:", error); throw error; });
}

/**
 * Normalises a status filter to the SaaS `status_ids` parameter.
 *
 * Always sends a list, never a bare integer: `ItemCountFulfiller` renders the value with
 * `format_sql_int_list(ids)` (no column name), which emits `(7)` for a list but a bare `7`
 * for a scalar — and `StatusID in 7` is not valid SQL.
 *
 * Status IDs, not names: `vwItemsAPI` derives its `Status` string with a CASE that stops at
 * 4, so every status from 5 upward reads as the literal 'Unknown' and matches no name filter.
 * See processingConfig.js for the full three-way name table.
 *
 * @param {number[]|number} [statusIds] - Status IDs to include; omit/empty for every status.
 * @returns {{status_ids: number[]}|{}} - Spreadable, `{}` when there is nothing to filter on.
 */
function _statusIdParam(statusIds) {
    if (statusIds === null || statusIds === undefined) return {};
    const arr = Array.isArray(statusIds) ? statusIds : [statusIds];
    const ints = arr.map(id => parseInt(id, 10)).filter(n => !isNaN(n));
    return ints.length ? {status_ids: ints} : {};
}

/**
 * Normalises the two free-text Metadata phrase filters to their SaaS parameters.
 *
 * One helper for both `man_item_count` and `man_item_list`, because the counters on the Media Metrics
 * card and the tape table a clicked tile opens have to ask the same question — a tile reading 12 above a
 * table of 14 rows is a contradiction with no way for a reader to tell which half is wrong. Sharing the
 * formatting is what keeps a phrase from being trimmed on one path and not the other.
 *
 * Blank phrases are dropped rather than sent: `like '%%'` matches every row that has any metadata at
 * all, so a blank include would report every annotated tape as a match and a blank exclude would hide
 * them all. Both fulfillers discard a blank themselves, but not sending it keeps it out of the cache key.
 *
 * Matched case-insensitively as a substring on the SaaS side, because Metadata is free text holding
 * several notes run together in one field and an equality test would match nothing.
 *
 * @param {string} [metadataIncludes] - Count/list ONLY media whose Metadata carries this phrase.
 * @param {string} [metadataExcludes] - Leave out media whose Metadata carries this phrase.
 * @returns {object} - Spreadable, `{}` when there is nothing to filter on.
 */
function _metadataFilterParams({metadataIncludes, metadataExcludes} = {}) {
    const includes = typeof metadataIncludes === 'string' ? metadataIncludes.trim() : '';
    const excludes = typeof metadataExcludes === 'string' ? metadataExcludes.trim() : '';
    return {
        ...(includes ? {metadata_includes: includes} : {}),
        ...(excludes ? {metadata_excludes: excludes} : {}),
    };
}

/**
 * One count, asked a second time if the first attempt does not come back.
 *
 * The dev deployment drops connections: browser → the UI container's nginx → the API app → Twingate → the
 * SaaS endpoint, seven to fourteen seconds per count, and one of a batch comes back 502 with an empty body
 * from that nginx having never reached the API at all — which the API's own logs confirm, every other
 * request of the same batch a 200. A second attempt needs a new connection rather than a quieter moment, so
 * it is immediate: nothing about waiting makes a dropped connection likelier to succeed, and it keeps this a
 * plain promise chain that fake-timer tests do not stall inside.
 *
 * Takes a thunk rather than a promise, so the retry can send the request again rather than re-await the one
 * that already failed.
 *
 * A cancellation is rethrown untouched — the answer is no longer wanted, and asking again would be worse
 * than not answering. Note that this check only became reachable once the response interceptor in index.js
 * stopped rewriting response-less errors as TypeErrors; before that fix this retried thirteen counts that
 * the Media Metrics card had just deliberately cancelled, which is what produced the 502s in the first
 * place.
 *
 * Two attempts, not more: a second failure is no longer a dropped connection but something wrong, and every
 * caller either polls or offers a Refresh.
 */
const _countRetriedOnce = (send) => Promise.resolve(send())
    .catch(error => {
        if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') throw error;
        console.warn("A tape count failed; asking once more:", error?.message ?? error);
        return send();
    });

/**
 * Counts one job's media, optionally narrowed to statuses and to the Metadata phrase filters.
 *
 * The phrase filters are here so that this can be the total behind a filtered tape table: `man_item_list`
 * reports the page's own row count as its `count`, not the size of the list, so a paged view cannot learn
 * its own length from the list call and has to ask for it. See {@link fetchJobTapeDataWithCount}.
 *
 * @param {number} cid - Company scope.
 * @param {number|string} jobId - The job to count within.
 * @param {number[]|number} [statusIds] - Status IDs to include; omit for every status.
 * @param {object} [options] - Passed to postDashboard, plus `metadataIncludes` / `metadataExcludes`
 *        as described on {@link _metadataFilterParams}.
 */
export function fetchJobTapeStatusCount(cid, jobId, statusIds, options = {}) {
    const {metadataIncludes, metadataExcludes, ...countOptions} = options;
    const request = {cid, job_id: jobId, ..._statusIdParam(statusIds), ..._metadataFilterParams({metadataIncludes, metadataExcludes})};
    return _countRetriedOnce(() => postDashboard('man_item_count', request, countOptions))
        .then(_extractCount)
        .catch(error => { console.error(error); throw error; });
}

/**
 * Counts tapes, optionally narrowed to a set of statuses, a job selection and a destroyed state.
 *
 * Scoping is handled by the SaaS ItemCountFulfiller from the `cid` and `job_id` we send:
 *   - Admin with no job selected → cid 0, which counts every SaaS company (the combined total).
 *   - Admin viewing one customer  → that customer's cid, narrowed to the selected job.
 *   - Client user                 → their own arkbridge_customer_id, only their own media.
 * Passing no cid falls through to _deriveCID(), which applies exactly that rule.
 *
 * @param {number[]} [statusIds] - Tape status IDs to include; omit/empty for every status.
 *        IDs, not names — see _statusIdParam above for why names cannot be used.
 * @param {number|string} [cid] - Optional explicit company scope.
 * @param {object} [options] - Passed to postDashboard (e.g. {signal}).
 * @param {number[]} [options.statusIds] - Same thing again, for a caller spreading a filter descriptor
 *        rather than naming each field; the positional argument takes precedence.
 * @param {number|number[]} [options.jobIds] - Optional job scope, matching the dashboard selector.
 * @param {boolean} [options.destroyed] - true counts only destroyed media, false only surviving
 *        media; omit to count both (the SaaS default).
 * @param {boolean} [options.encrypted] - true counts only media the RFID transponder reports as
 *        encrypted, false only the rest; omit to count both.
 * @param {string} [options.metadataExcludes] - Phrase to exclude, matched case-insensitively as a
 *        substring of the item's Metadata (the 'Comments' column in the portal). Tapes carrying the
 *        phrase are left out of the count. Metadata is free text holding several notes at once, so
 *        this is a contains-test, not an equality test.
 * @param {string} [options.metadataIncludes] - The inverse: count ONLY tapes whose Metadata carries
 *        the phrase. Same case-insensitive substring compare. Drives the Damaged tile, where the
 *        condition itself is what is being reported rather than filtered away.
 */
export function fetchTapeStatusCount(statusIds, cid, options = {}) {
    const {jobIds, destroyed, encrypted, metadataExcludes, metadataIncludes, statusIds: statusIdsOption, ...requestOptions} = options;
    const destroyedParam = typeof destroyed === 'boolean' ? {destroyed} : {};
    // Omitted rather than sent as false, so the count stays unconstrained for every existing caller.
    const encryptedParam = typeof encrypted === 'boolean' ? {encrypted} : {};
    // The two phrase filters, formatted by the same helper the list call uses. Blank phrases are dropped
    // there, not here — '%%' matches every annotated tape whichever way round it is applied.
    const metadataParams = _metadataFilterParams({metadataIncludes, metadataExcludes});
    /*
     * The positional argument wins, and an options.statusIds is the fallback.
     *
     * Accepted in the options at all so that a filter descriptor — which names its fields as this
     * function names its options, statuses included — spreads onto the request whole rather than having
     * its status list peeled off and passed separately. Without this, the descriptor's `statusIds` would
     * land in `requestOptions` and be handed to postDashboard as if it were a `signal` or a `cache` flag.
     */
    const effectiveStatusIds = statusIds ?? statusIdsOption;
    return postDashboard('man_item_count', {..._statusIdParam(effectiveStatusIds), ...destroyedParam, ...encryptedParam, ...metadataParams, ..._jobIdParam(jobIds), ..._cidParam(cid)}, requestOptions)
        .then(_extractCount)
        .catch(error => { console.error("fetchTapeStatusCount error:", error); throw error; });
}

export function fetchTapeDetails(cid, itemId, options = {}) {
    return postDashboard('man_item_get', {cid, item_id: itemId}, options)
        .then(_extractEntry)
        .catch(error => { console.error(error); throw error; });
}

/**
 * The photographs of one tape: every one ArkWeb holds, in the order the endpoint returns them, and an empty
 * list when there are none.
 *
 * `man_item_image_get` with an `item_id` answers one entry per image, each with its own presigned URL. Only
 * the first used to be kept, so a tape photographed three times showed one. Entries without a URL are
 * dropped here: an <img> with no src draws a broken-image icon (see the popup).
 *
 * Quiet by default, because a tape without a photograph is ordinary — plenty of media was catalogued
 * before the cameras, and `man_item_image_get` is not registered at all on a local deployment
 * (proxy_lambda.py, where it is skipped along with the S3 presigning it needs), so there the request
 * answers 400 for every tape. The popup shows that there is no photograph, which is the whole of the
 * news; a red banner quoting the upstream error on top of it would say the page is broken instead. The
 * option is still spread last, so a caller that wants the failure reported can ask for it.
 *
 * `quiet` silences the console too, and not only the banner. It did not, which made a local deployment
 * print a stack for every tape whose details were opened — three lines each between this, the caller, and
 * the browser's own report of the 400 — until the console was too full to see a real fault in. A request
 * whose failure is expressly not news should not be the loudest thing in there. Still rethrown either way:
 * the caller decides what to show, and swallowing it here would leave the popup waiting for a photograph
 * that is never coming.
 */
export function fetchTapeImageDetails(cid, itemId, options = {}) {
    const {quiet = true} = options;
    return postDashboard('man_item_image_get', {cid, item_id: itemId}, {...options, quiet})
        .then(data => {
            const entry = _extractEntry(data);
            const entries = Array.isArray(entry) ? entry : (entry ? [entry] : []);
            return entries.filter(image => image?.presigned_url);
        })
        .catch(error => { if (!quiet) console.error(error); throw error; });
}

// ── Dashboard Reports ────────────────────────────────────────────────────────

export function fetchIngestedTimelineData(jobIds, cid, options = {}) {
    return postDashboard('rep_ingest', {..._jobIdParam(jobIds), ..._cidParam(cid)}, options);
}

export function fetchAgeingData(jobIds, cid, options = {}) {
    return postDashboard('rep_ageing', {..._jobIdParam(jobIds), ..._cidParam(cid)}, options);
}

export function fetchPolicyData(jobIds, cid, options = {}) {
    return postDashboard('rep_policy', {..._jobIdParam(jobIds), ..._cidParam(cid)}, options);
}

export function fetchBackupData(jobIds, cid, options = {}) {
    return postDashboard('rep_format', {..._jobIdParam(jobIds), ..._cidParam(cid)}, options);
}

// ── Server Data ──────────────────────────────────────────────────────────────

export function fetchServerData(jobIds, cid) {
    return postDashboard('rep_server', {..._jobIdParam(jobIds), ..._cidParam(cid)})
        .then(_extractRows)
        .catch(error => { console.error(error); throw error; });
}

// ── Hydration & Company ──────────────────────────────────────────────────────

/*
 * Both of these answer "what can this deployment do", which the dashboard asks on mount to decide
 * whether the Files focus is available at all. Nobody typed anything to cause them, so they take
 * `options` for the same `background: true` the widgets pass — unmarked they held the loading wash
 * over the whole portal, sidebar included, for the length of two round trips out to ArkBridge.
 */
export function fetchHydrateStatus(options = {}) {
    return postDashboard('restore_data_status', {}, {cache: false, ...options});
}

export function fetchDeploymentType(options = {}) {
    return postDashboard('deployment_type', {}, options);
}

export function fetchJobList(arkbridgeCustomerId) {
    if (!arkbridgeCustomerId) throw new Error("ArkBridge Customer ID is required");
    return postDashboard('man_job_list', {cid: parseInt(arkbridgeCustomerId, 10)});
}

export function fetchCompanyList() {
    return postDashboard('man_company_list', {page_size: 1000});
}

// The SaaS gates *chargeable* hydration on the company's marketplace registration and refuses
// the request with "Cannot initiate chargeable hydration: company marketplace registration has
// not been received yet" until that arrives. Registration is still being built, so requests go
// out non-chargeable to keep the portal flow usable in the meantime.
//
// TODO: set back to "true" once marketplace registration is live. Hydrations raised while this
// is "false" are not billed. The assertion in api.hydration.checked.test.jsx pins this value on
// purpose, so flipping it without updating the test fails loudly rather than silently.
const HYDRATION_CHARGEABLE = "false";

export function requestHydration(expiresUTCString) {
    return postDashboard('hydrate_restore_data', {
        chargeable: HYDRATION_CHARGEABLE,
        initiator: "customer",
        reason: "Nexus portal hydration request",
        expires: expiresUTCString,
    }, {cache: false});
}
